import * as React from 'react';
import { keyBy } from 'lodash';

import Slider from 'react-slick';
import Spinner from 'components/spinner';
import Lightbox from 'components/lightbox';
import ProgressBlockTier from './progress-block-tier';
import ProgressBlockHeader from './progress-block-header';
import ProgressBlockContainer from './progress-block-container';
import ProgressBlockExpandButton from './progress-block-expand-button';
import { ProgressBlockBar, IProgressBarStep } from './progress-block-bar';
import { Icon, ICON_NAME } from 'components/icon';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

import { IProgressBlockProps } from './progress-block.types';
import { IRewardsResponseItem } from '@sitegen/common/classes/friends-referral-api';
import { IRewardTierItem } from '@sitegen/common/types/rewards-tiers';

import { REWARDS_TIER_TEXT_KEY_PREFIX } from '@sitegen/common/constants/website-content';
import TEXT_KEYS, {
    REWARD_TIERS_NUMBER,
    REWARD_TIERS_MOBILE_NUMBER,
    OPEN_ALL_REWARDS_ANALYTICS,
    OPEN_REWARD_ITEM_ANALYTICS
} from './progress-block.constants';

import rewardPlaceholder from '../../assets/reward-placeholder.svg';
import './progress-block.styles.less';

class ProgressBlock extends React.Component<IProgressBlockProps, IProgressBlockState> {
    public state: IProgressBlockState = {
        currentSlide: 0,
        expandButtonOpened: false,
        lightboxOpened: false
    };

    private getAllProgressTiers = (): JSX.Element[] => {
        const { rewardTiers, rewardTiersAssets, dangerouslyTranslateWithHTML: translateKey } = this.props;
        const assetsMap = keyBy(rewardTiersAssets, asset => asset.tierId);

        return Object.keys(rewardTiers)
            .map((levelNumber, index) => {
                const asset = assetsMap[levelNumber] as IRewardTierItem | undefined;
                return <ProgressBlockTier
                    key={levelNumber}
                    imageSrc={asset && asset.imageSrc}
                    title={translateKey(`${REWARDS_TIER_TEXT_KEY_PREFIX}${levelNumber}`)}
                    onClick={() => {
                        this.openLightboxWithImage(asset);
                        const rewardItemId: string = ((asset && asset.tierId) || ++index).toString();
                        AnalyticsService.sendClickEvent(OPEN_REWARD_ITEM_ANALYTICS(rewardItemId));
                    }}
                />;
            });
    };

    private getInvitedFriendsCount = (): number => {
        return this.props.invitees.filter(
            ({ hasStats, hasOwnership }) => hasStats && hasOwnership
        ).length;
    };

    private openLightboxWithImage = (asset?: IRewardTierItem) => {
        this.setState({
            currentImage: asset ? asset.imageSrc : rewardPlaceholder,
            lightboxOpened: true
        });
    };

    private setActualSlideIndex = (_: Object, index: number) => {
        this.setState({
            currentSlide: index
        });
    };

    private closeLightbox = () => {
        this.setState({
            lightboxOpened: false
        });
    };

    private getTiersSlider = (): JSX.Element => {
        const sliderSettings = {
            slidesToShow: this.getSteps().length,
            infinite: false,
            beforeChange: this.setActualSlideIndex,
            nextArrow: this.getArrowButton(),
            prevArrow: this.getArrowButton(true)
        };
        return (
            <Slider {...sliderSettings}>
                {this.getAllProgressTiers()}
            </Slider>
        );
    };

    private getStepFromReward = (reward: IRewardsResponseItem): IProgressBarStep => {
        const translateKey = this.props.dangerouslyTranslateWithHTML;
        const { levelLimit } = reward;
        const title = levelLimit === 1
            ? translateKey(TEXT_KEYS.ONE_FRIEND)
            : translateKey(TEXT_KEYS.PLURAL_FRIENDS, { amount: levelLimit });

        return { title, count: levelLimit };
    };

    private getSteps = (): IProgressBarStep[] => {
        const rewards = Object.values(this.props.rewardTiers);
        const res: IProgressBarStep[] = [];

        for (let i = 0; i < REWARD_TIERS_NUMBER; ++i) {
            const currentReward: undefined | IRewardsResponseItem = rewards[i + this.state.currentSlide];
            if (currentReward) {
                res.push(this.getStepFromReward(currentReward));
            }
        }

        return res;
    };

    private getMobileSteps = (): IProgressBarStep[] => {
        const allSteps = Object.values(this.props.rewardTiers)
            .map(this.getStepFromReward);

        return this.state.expandButtonOpened
            ? allSteps
            : allSteps.filter((_, index) => index < REWARD_TIERS_MOBILE_NUMBER);
    };

    private getArrowButton = (reverseDirection = false): JSX.Element => {
        const iconName = reverseDirection ? ICON_NAME.ANGLE_LEFT : ICON_NAME.ANGLE_RIGHT;
        return (
            <div>
                <Icon name={iconName}/>
            </div>
        );
    };

    private onExpandButtonClick = () => {
        this.setState(prevState => ({
            expandButtonOpened: !prevState.expandButtonOpened
        }));
    };

    private getMobileProgressTiers = (): JSX.Element[] => {
        return this.state.expandButtonOpened
            ? this.getAllProgressTiers()
            : this.getAllProgressTiers()
                .filter((_, index) => index < REWARD_TIERS_MOBILE_NUMBER);
    };

    private openRewardsLightbox = () => {
        this.props.showRewardsImageLightbox(true);
        AnalyticsService.sendClickEvent(OPEN_ALL_REWARDS_ANALYTICS);
    };

    private getLightbox = (): React.ReactNode => {
        return this.state.lightboxOpened && this.state.currentImage && (
            <Lightbox
                onCloseRequest={this.closeLightbox}
                mainSrc={this.state.currentImage}
            />
        );
    };

    private canSlidePrev = (): boolean => {
        return this.state.currentSlide > 0;
    };

    private canSlideNext = (): boolean => {
        const alreadyShowed = REWARD_TIERS_NUMBER + this.state.currentSlide;
        return alreadyShowed < this.getAllProgressTiers().length;
    };

    private getExpandButton = (): React.ReactNode => {
        const { expandButtonOpened } = this.state;
        const { dangerouslyTranslateWithHTML } = this.props;

        return REWARD_TIERS_MOBILE_NUMBER < this.getAllProgressTiers().length
            ? <div className="sitegen-friend-referral-progress-block-expand">
                <ProgressBlockExpandButton
                    onClick={() => this.onExpandButtonClick()}
                    iconName={expandButtonOpened ? ICON_NAME.ANGLE_TOP : ICON_NAME.ANGLE_DOWN}
                    text={
                        expandButtonOpened ?
                            dangerouslyTranslateWithHTML(TEXT_KEYS.CLOSE_SEE_MORE) :
                            dangerouslyTranslateWithHTML(TEXT_KEYS.SEE_MORE)
                    }
                />
            </div>
            : null;
    };

    private getProgressBlock = () => {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;

        return (
            <div className="sitegen-friend-referral-progress-block">
                {this.getLightbox()}

                <ProgressBlockHeader
                    title={translateKey(TEXT_KEYS.TITLE)}
                    subtitle={translateKey(TEXT_KEYS.SUBTITLE)}
                    link={translateKey(TEXT_KEYS.SEE_FULL)}
                    onClick={this.openRewardsLightbox}
                />

                <ProgressBlockContainer>
                    {this.getTiersSlider()}
                </ProgressBlockContainer>

                <div className="sitegen-friend-referral-progress-block-bar">
                    <ProgressBlockBar
                        value={this.getInvitedFriendsCount()}
                        steps={this.getSteps()}
                        leftTail={this.canSlidePrev()}
                        rightTail={this.canSlideNext()}
                    />
                </div>

                <div className="sitegen-friend-referral-progress-block-mobile">
                    <div className="sitegen-friend-referral-progress-block-mobile__inner">
                        <div className="sitegen-friend-referral-progress-block-mobile__bar">
                            <ProgressBlockBar
                                value={this.getInvitedFriendsCount()}
                                steps={this.getMobileSteps()}
                                vertical
                            />
                        </div>

                        <div className="sitegen-friend-referral-progress-block-mobile__container">
                            {this.getMobileProgressTiers()}
                        </div>
                    </div>

                    {this.getExpandButton()}
                </div>
            </div>
        );
    };

    public render() {
        return this.props.loading ? <Spinner/> : this.getProgressBlock();
    }
}

interface IProgressBlockState {
    readonly currentSlide: number;
    readonly expandButtonOpened: boolean;
    readonly currentImage?: string;
    readonly lightboxOpened: boolean;
}

export default ProgressBlock;
export {
    Slider,
    Spinner,
    ProgressBlockBar,
    ProgressBlockHeader,
    ProgressBlockContainer,
    ProgressBlockExpandButton,
    IProgressBlockProps
};
